import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Users, 
  Activity, 
  Clock, 
  Eye, 
  MousePointer,
  RefreshCw,
  User,
  LogIn,
  LogOut,
  Monitor,
  ChevronLeft,
  ChevronRight,
  Search,
  BarChart3,
  FileText,
  Download,
  CalendarDays
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#00D3A8', '#00A8E8', '#FF6B6B', '#FFE66D', '#4ECDC4', '#95E1D3', '#F38181', '#AA96DA'];

const ROLE_LABELS = {
  superadmin: 'Super Admin',
  admin: 'Admin',
  gestionnaire: 'Gestionnaire',
  directeur_cabinet: 'Directeur de cabinet',
  coproprietaire: 'Copropriétaire',
  prestataire: 'Prestataire',
  assistant: 'Assistant'
};

const PAGE_LABELS = {
  '/': 'Accueil',
  '/comptabilite': 'Comptabilité',
  '/messagerie': 'Messagerie',
  '/incidents': 'Incidents',
  '/assemblees': 'Assemblées',
  '/immeubles': 'Immeubles',
  '/coproprietaires': 'Copropriétaires',
  '/equipe': 'Équipe',
  '/prestataires': 'Prestataires',
  '/visites': 'Visites',
  '/carnet-entretien': 'Carnet d\'entretien',
  '/evenements': 'Événements',
  '/annonces': 'Annonces',
  '/mon-compte': 'Mon Compte',
  '/admin': 'Admin Dashboard',
  '/admin/cabinets': 'Cabinets',
  '/admin/utilisateurs': 'Utilisateurs',
  '/admin/activite': 'Tracker d\'activité'
};

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const ActivityTracker = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('realtime');
  const [stats, setStats] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [sessions, setSessions] = useState({ sessions: [], total: 0 });
  const [logs, setLogs] = useState({ logs: [], total: 0 });
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [period, setPeriod] = useState(7);
  const [sessionPage, setSessionPage] = useState(1);
  const [logPage, setLogPage] = useState(1);
  const [emailFilter, setEmailFilter] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Tabs configuration - Same style as Assemblées
  const tabs = [
    { id: 'realtime', label: 'Temps réel' },
    { id: 'sessions', label: 'Historique sessions' },
    { id: 'logs', label: 'Journal détaillé' },
    { id: 'stats', label: 'Statistiques' },
    { id: 'summary', label: 'Résumé mensuel' }
  ];

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/activity/stats?days=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token, period]);

  const fetchOnlineUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/activity/online-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOnlineUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching online users:', error);
    }
  }, [token]);

  const fetchSessions = useCallback(async () => {
    try {
      let url = `${API_URL}/api/activity/sessions?page=${sessionPage}&limit=20`;
      if (emailFilter) url += `&user_email=${encodeURIComponent(emailFilter)}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  }, [token, sessionPage, emailFilter]);

  const fetchLogs = useCallback(async () => {
    try {
      let url = `${API_URL}/api/activity/logs?page=${logPage}&limit=50`;
      if (emailFilter) url += `&user_email=${encodeURIComponent(emailFilter)}`;
      if (actionTypeFilter) url += `&action_type=${actionTypeFilter}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  }, [token, logPage, emailFilter, actionTypeFilter]);

  const fetchMonthlySummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/activity/monthly-summary?month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMonthlySummary(data);
      }
    } catch (error) {
      console.error('Error fetching monthly summary:', error);
    }
  }, [token, selectedMonth]);

  const fetchUserDetail = useCallback(async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/activity/user/${userId}?days=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserDetail(data);
      }
    } catch (error) {
      console.error('Error fetching user detail:', error);
    }
  }, [token, period]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchOnlineUsers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchOnlineUsers]);

  useEffect(() => {
    if (activeTab === 'sessions') fetchSessions();
  }, [activeTab, fetchSessions]);

  useEffect(() => {
    if (activeTab === 'logs') fetchLogs();
  }, [activeTab, fetchLogs]);

  useEffect(() => {
    if (activeTab === 'summary') fetchMonthlySummary();
  }, [activeTab, fetchMonthlySummary]);

  useEffect(() => {
    if (selectedUser) fetchUserDetail(selectedUser);
  }, [selectedUser, fetchUserDetail]);

  // Auto-refresh online users every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchOnlineUsers, 30000);
    return () => clearInterval(interval);
  }, [fetchOnlineUsers]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchStats(), fetchOnlineUsers(), fetchSessions(), fetchLogs()]);
    if (activeTab === 'summary') await fetchMonthlySummary();
    setRefreshing(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'login': return <LogIn size={14} className="text-green-500" />;
      case 'logout': return <LogOut size={14} className="text-red-500" />;
      case 'page_view': return <Eye size={14} className="text-blue-500" />;
      case 'action': return <MousePointer size={14} className="text-purple-500" />;
      default: return <Activity size={14} className="text-gray-500" />;
    }
  };

  const getPageLabel = (page) => PAGE_LABELS[page] || page || '-';

  const exportToExcel = async () => {
    if (!monthlySummary) return;
    
    try {
      const response = await fetch(`${API_URL}/api/activity/export/excel?month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapport-activite-${selectedMonth}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  const exportToPDF = async () => {
    if (!monthlySummary) return;
    
    try {
      const response = await fetch(`${API_URL}/api/activity/export/pdf?month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapport-activite-${selectedMonth}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting to PDF:', error);
    }
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
      options.push({ value, label });
    }
    return options;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00D3A8] border-t-transparent mx-auto mb-2"></div>
          <p className="text-gray-500">Chargement des données d'activité...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="activity-tracker">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Activity className="text-[#00D3A8]" />
            Tracker d'Activité
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Surveillance en temps réel de l'activité des utilisateurs (rétention: 30 jours)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
          >
            <option value={7}>7 derniers jours</option>
            <option value={14}>14 derniers jours</option>
            <option value={30}>30 derniers jours</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs md:text-sm">En ligne maintenant</p>
                <p className="text-2xl md:text-3xl font-bold text-[#00D3A8] mt-1">{stats.summary.currently_online}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-50 rounded-full flex items-center justify-center">
                <Monitor className="text-[#00D3A8]" size={20} />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs md:text-sm">Sessions ({period}j)</p>
                <p className="text-2xl md:text-3xl font-bold text-blue-500 mt-1">{stats.summary.total_sessions}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-50 rounded-full flex items-center justify-center">
                <Users className="text-blue-500" size={20} />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs md:text-sm">Utilisateurs uniques</p>
                <p className="text-2xl md:text-3xl font-bold text-purple-500 mt-1">{stats.summary.unique_users}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-50 rounded-full flex items-center justify-center">
                <User className="text-purple-500" size={20} />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs md:text-sm">Durée moy. session</p>
                <p className="text-xl md:text-2xl font-bold text-amber-500 mt-1">{stats.summary.avg_session_duration_formatted}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-50 rounded-full flex items-center justify-center">
                <Clock className="text-amber-500" size={20} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content with Tabs - Same style as Assemblées */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Tabs - Style unifié avec coins arrondis en haut */}
        <div className="border-b border-gray-200 px-4 md:px-6">
          <div className="flex flex-wrap gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-2 text-sm font-medium transition-all rounded-t-lg ${
                  activeTab === tab.id
                    ? 'bg-[#00D3A8] text-white'
                    : 'text-gray-600 hover:bg-gray-100 bg-gray-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 md:p-6">
          {/* Real-time Tab */}
          {activeTab === 'realtime' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Utilisateurs en ligne ({onlineUsers.length})
                </h3>
                <span className="flex items-center gap-2 text-sm text-green-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Actualisation auto toutes les 30s
                </span>
              </div>
              
              {onlineUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Monitor className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Aucun utilisateur en ligne actuellement</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Utilisateur</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Rôle</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Page actuelle</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Connecté depuis</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Durée</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {onlineUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                                <span className="text-[#00D3A8] font-medium text-sm">
                                  {user.user_name?.charAt(0) || user.user_email?.charAt(0) || '?'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{user.user_name || 'N/A'}</p>
                                <p className="text-xs text-gray-500">{user.user_email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.user_role === 'superadmin' ? 'bg-red-100 text-red-700' :
                              user.user_role === 'admin' ? 'bg-purple-100 text-purple-700' :
                              user.user_role === 'gestionnaire' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {ROLE_LABELS[user.user_role] || user.user_role}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600">{getPageLabel(user.current_page)}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatTime(user.login_time)}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-medium text-[#00D3A8]">{user.duration_formatted}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-500">{user.actions_count} actions</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Sessions Tab */}
          {activeTab === 'sessions' && (
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Rechercher par email..."
                    value={emailFilter}
                    onChange={(e) => { setEmailFilter(e.target.value); setSessionPage(1); }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Utilisateur</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Rôle</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Connexion</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Déconnexion</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Durée</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Pages</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sessions.sessions.map((session) => (
                      <tr 
                        key={session.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedUser(session.user_id)}
                      >
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-800">{session.user_name || 'N/A'}</p>
                            <p className="text-xs text-gray-500">{session.user_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            session.user_role === 'superadmin' ? 'bg-red-100 text-red-700' :
                            session.user_role === 'admin' ? 'bg-purple-100 text-purple-700' :
                            session.user_role === 'gestionnaire' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {ROLE_LABELS[session.user_role] || session.user_role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(session.login_time)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {session.is_active ? (
                            <span className="flex items-center gap-1 text-green-600">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              En ligne
                            </span>
                          ) : formatDate(session.logout_time)}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-[#00D3A8]">{session.duration_formatted || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{session.pages_visited?.length || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{session.actions_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {sessions.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Page {sessions.page} sur {sessions.total_pages} ({sessions.total} sessions)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSessionPage(p => Math.max(1, p - 1))}
                      disabled={sessionPage === 1}
                      className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setSessionPage(p => Math.min(sessions.total_pages, p + 1))}
                      disabled={sessionPage === sessions.total_pages}
                      className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Rechercher par email..."
                    value={emailFilter}
                    onChange={(e) => { setEmailFilter(e.target.value); setLogPage(1); }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  />
                </div>
                <select
                  value={actionTypeFilter}
                  onChange={(e) => { setActionTypeFilter(e.target.value); setLogPage(1); }}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                >
                  <option value="">Tous les types</option>
                  <option value="login">Connexions</option>
                  <option value="logout">Déconnexions</option>
                  <option value="page_view">Pages vues</option>
                  <option value="action">Actions</option>
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Heure</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Utilisateur</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Page</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Détail</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {logs.logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(log.timestamp)}</td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{log.user_name || 'N/A'}</p>
                            <p className="text-xs text-gray-500">{log.user_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-2">
                            {getActionIcon(log.action_type)}
                            <span className="text-sm text-gray-600 capitalize">{log.action_type?.replace('_', ' ')}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{getPageLabel(log.page)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{log.action_detail || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {logs.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Page {logs.page} sur {logs.total_pages} ({logs.total} événements)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setLogPage(p => Math.max(1, p - 1))}
                      disabled={logPage === 1}
                      className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setLogPage(p => Math.min(logs.total_pages, p + 1))}
                      disabled={logPage === logs.total_pages}
                      className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && stats && (
            <div className="space-y-8">
              {/* Sessions over time */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Sessions par jour</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={stats.daily_sessions}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="sessions" 
                        stroke="#00D3A8" 
                        fill="#00D3A8" 
                        fillOpacity={0.3}
                        name="Sessions"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Top Pages */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Pages les plus visitées</h3>
                  <div className="space-y-3">
                    {stats.top_pages.map((page, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="text-sm text-gray-500 w-6">{idx + 1}.</span>
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700">{getPageLabel(page.page)}</span>
                            <span className="text-sm text-gray-500">{page.views} vues</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div 
                              className="bg-[#00D3A8] h-2 rounded-full" 
                              style={{ width: `${(page.views / stats.top_pages[0]?.views) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sessions by Role */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Sessions par rôle</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stats.sessions_by_role.map(r => ({
                            ...r,
                            name: ROLE_LABELS[r.role] || r.role
                          }))}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="count"
                          nameKey="name"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {stats.sessions_by_role.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Hourly Distribution */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Distribution horaire</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.hourly_distribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickFormatter={(h) => `${h}h`} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip 
                          labelFormatter={(h) => `${h}:00 - ${h}:59`}
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="sessions" fill="#00A8E8" name="Sessions" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Most Active Users */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Utilisateurs les plus actifs</h3>
                  <div className="space-y-2">
                    {stats.most_active_users.slice(0, 5).map((user, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                        onClick={() => setSelectedUser(user.user_id)}
                      >
                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                          <span className="text-[#00D3A8] font-medium text-sm">
                            {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800">{user.name || 'N/A'}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-[#00D3A8]">{user.actions}</p>
                          <p className="text-xs text-gray-500">actions</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Top Actions */}
              {stats.top_actions.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Actions les plus fréquentes</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {stats.top_actions.slice(0, 10).map((action, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-[#00D3A8]">{action.count}</p>
                        <p className="text-xs text-gray-500 mt-1 truncate">{action.action}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Monthly Summary Tab */}
          {activeTab === 'summary' && (
            <div>
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div className="flex items-center gap-3">
                  <CalendarDays className="text-[#00D3A8]" size={24} />
                  <h3 className="text-lg font-semibold text-gray-800">Résumé mensuel</h3>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  >
                    {getMonthOptions().map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={exportToExcel}
                    disabled={!monthlySummary}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    title="Exporter en Excel"
                  >
                    <Download size={16} />
                    Excel
                  </button>
                  <button
                    onClick={exportToPDF}
                    disabled={!monthlySummary}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    title="Exporter en PDF"
                  >
                    <FileText size={16} />
                    PDF
                  </button>
                </div>
              </div>

              {monthlySummary ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-5">
                      <p className="text-teal-700 text-sm font-medium">Sessions totales</p>
                      <p className="text-3xl font-bold text-teal-800 mt-2">{monthlySummary.summary.total_sessions}</p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5">
                      <p className="text-blue-700 text-sm font-medium">Utilisateurs uniques</p>
                      <p className="text-3xl font-bold text-blue-800 mt-2">{monthlySummary.summary.unique_users}</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5">
                      <p className="text-purple-700 text-sm font-medium">Actions totales</p>
                      <p className="text-3xl font-bold text-purple-800 mt-2">{monthlySummary.summary.total_actions}</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-5">
                      <p className="text-amber-700 text-sm font-medium">Durée moy. session</p>
                      <p className="text-2xl font-bold text-amber-800 mt-2">{monthlySummary.summary.avg_session_duration_formatted}</p>
                    </div>
                  </div>

                  {/* Daily Chart */}
                  <div className="bg-gray-50 rounded-xl p-5">
                    <h4 className="text-md font-semibold text-gray-800 mb-4">Sessions par jour</h4>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={monthlySummary.daily_sessions}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Area 
                            type="monotone" 
                            dataKey="sessions" 
                            stroke="#00D3A8" 
                            fill="#00D3A8" 
                            fillOpacity={0.3}
                            name="Sessions"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top Pages */}
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h4 className="text-md font-semibold text-gray-800 mb-4">Pages les plus visitées</h4>
                      <div className="space-y-3">
                        {monthlySummary.top_pages?.slice(0, 8).map((page, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">{idx + 1}. {getPageLabel(page.page)}</span>
                            <span className="text-sm font-medium text-[#00D3A8]">{page.views} vues</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Most Active Users */}
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h4 className="text-md font-semibold text-gray-800 mb-4">Utilisateurs les plus actifs</h4>
                      <div className="space-y-3">
                        {monthlySummary.most_active_users?.slice(0, 8).map((user, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-500">{idx + 1}.</span>
                              <span className="text-sm text-gray-700">{user.name || user.email}</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                user.role === 'superadmin' ? 'bg-red-100 text-red-700' :
                                user.role === 'gestionnaire' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {ROLE_LABELS[user.role] || user.role}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-[#00D3A8]">{user.actions} actions</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Sessions by Role */}
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h4 className="text-md font-semibold text-gray-800 mb-4">Sessions par rôle</h4>
                      <div className="space-y-3">
                        {monthlySummary.sessions_by_role?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">{ROLE_LABELS[item.role] || item.role}</span>
                            <span className="text-sm font-medium text-blue-600">{item.count} sessions</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Peak Hours */}
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h4 className="text-md font-semibold text-gray-800 mb-4">Heures de pointe</h4>
                      <div className="h-32">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={monthlySummary.hourly_distribution}>
                            <XAxis dataKey="hour" tick={{ fontSize: 9 }} tickFormatter={(h) => `${h}h`} />
                            <YAxis tick={{ fontSize: 9 }} />
                            <Tooltip labelFormatter={(h) => `${h}:00`} />
                            <Bar dataKey="sessions" fill="#00A8E8" radius={[2, 2, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Chargement du résumé mensuel...</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* User Detail Modal */}
      {selectedUser && userDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-800">{userDetail.user?.name || 'Utilisateur'}</h2>
                <p className="text-sm text-gray-500">{userDetail.user?.email}</p>
              </div>
              <button
                onClick={() => { setSelectedUser(null); setUserDetail(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* User Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-[#00D3A8]">{userDetail.stats.total_sessions}</p>
                  <p className="text-xs text-gray-500">Sessions</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{userDetail.stats.total_actions}</p>
                  <p className="text-xs text-gray-500">Actions</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-xl font-bold text-purple-600">{userDetail.stats.total_time_formatted}</p>
                  <p className="text-xs text-gray-500">Temps total</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-xl font-bold text-amber-600">{userDetail.stats.avg_session_time_formatted}</p>
                  <p className="text-xs text-gray-500">Moy./session</p>
                </div>
              </div>

              {/* Top Pages */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Pages favorites</h3>
                <div className="flex flex-wrap gap-2">
                  {userDetail.top_pages.map((page, idx) => (
                    <span key={idx} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm">
                      {getPageLabel(page.page)} ({page.views})
                    </span>
                  ))}
                </div>
              </div>

              {/* Recent Activity */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Activité récente</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {userDetail.recent_activities.slice(0, 20).map((activity, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm py-2 border-b border-gray-50">
                      {getActionIcon(activity.action_type)}
                      <span className="text-gray-600">{formatDate(activity.timestamp)}</span>
                      <span className="text-gray-800">{getPageLabel(activity.page)}</span>
                      {activity.action_detail && (
                        <span className="text-gray-500">- {activity.action_detail}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTracker;
